import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useQuery } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import {
  DM_Material,
  Material as MaterialModel,
  ProductionType as ProductionTypeModel,
  WorkOrder as WorkOrderModel,
} from "../../odata/appu4dm/AppU4DMModel";
import { qWorkOrder, QWorkOrder } from "../../odata/appu4dm/QAppU4DM";
import { useWorkOrderOperation } from "./use-work-order-operation";
import { workOrderKeys } from "./work-order-keys";

export function useWorkOrder() {
  const { workOrderOperation } = useWorkOrderOperation();

  const { data, ...query } = useQuery({
    queryKey: workOrderKeys.get(workOrderOperation?.WorkOrder_Id),
    queryFn: ({ meta }) =>
      fetchWorkOrder(workOrderOperation!.WorkOrder_Id!, meta!.token!),
    enabled: ({ meta }) => !!(workOrderOperation?.WorkOrder_Id && meta?.token),
  });

  return {
    workOrder: data?.value[0],
    ...query,
  };
}

type Material = Pick<
  MaterialModel,
  | "Id"
  | "Revision"
  | "SourceRevision"
  | "IsCurrent"
  | "UId"
  | "NId"
  | "Name"
  | "Description"
  | "UoMNId"
  | "TemplateNId"
>;

type FinalMaterial = Pick<
  DM_Material,
  | "Id"
  | "SerialNumberProfile"
  | "FirstArticleInspection"
  | "IsPhantom"
  | "LogisticClassNId"
  | "CorrelationId"
  | "EffectivityExpression"
  | "Traceable"
  | "Material_Id"
  | "MaterialClass_Id"
  | "Supplier_Id"
  | "FunctionalCode_Id"
  | "Weight"
  | "Volume"
  | "Material"
> & { Material: Material };

type ProductionType = Pick<ProductionTypeModel, "NId">;

type WorkOrder = Pick<
  WorkOrderModel,
  | "Id"
  | "Name"
  | "NId"
  | "FinalMaterial_Id"
  | "InitialQuantity"
  | "Enterprise"
  | "Plant"
  | "InitialQuantity"
  | "PlannedTargetQuantity"
  | "ActualTargetQuantity"
  | "TargetQuantity"
> & { FinalMaterial: FinalMaterial; ProductionType: ProductionType };

async function fetchWorkOrder(workOrderId: string, token: string) {
  const builder = createQueryBuilderV4("WorkOrder", new QWorkOrder())
    .filter(qWorkOrder.Id.eq(workOrderId))
    .select(
      "Id",
      "Name",
      "NId",
      "FinalMaterial_Id",
      "InitialQuantity",
      "Enterprise",
      "Plant",
      "InitialQuantity",
      "PlannedTargetQuantity",
      "ActualTargetQuantity",
      "TargetQuantity"
    )
    .expanding("FinalMaterial", (b) =>
      b
        .expanding("Material", (e) =>
          e.select(
            "Id",
            "Revision",
            "SourceRevision",
            "IsCurrent",
            "UId",
            "NId",
            "Name",
            "Description",
            "UoMNId",
            "TemplateNId"
          )
        )
        .select(
          "Id",
          "SerialNumberProfile",
          "FirstArticleInspection",
          "IsPhantom",
          "LogisticClassNId",
          "CorrelationId",
          "EffectivityExpression",
          "Traceable",
          "Material_Id",
          "MaterialClass_Id",
          "Supplier_Id",
          "FunctionalCode_Id",
          "Weight",
          "Volume",
          "Material"
        )
    )
    .expanding("ProductionType", (b) => b.select("NId"));

  return odataFetch<WorkOrder>({ builder, token });
}
